/**
 * Redirection field rules shared by the API (write-side validation), the
 * engine (read-side interpretation) and the UI.
 */

/** HTTP status codes a redirection may answer with. */
export const REDIRECT_HTTP_CODES = [301, 302, 307, 308] as const;
export type RedirectHttpCode = (typeof REDIRECT_HTTP_CODES)[number];

/** How the incoming query string is handled. */
export const QUERY_PARAMS_MODES = ['preserve', 'drop'] as const;
export type QueryParamsMode = (typeof QUERY_PARAMS_MODES)[number];

/**
 * URL schemes a redirect target may use: web pages plus the mailto:, tel:
 * and sms: links produced by the UI's contact templates.
 */
export const TARGET_URL_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:', 'sms:'] as const;

export function isRedirectHttpCode(value: unknown): value is RedirectHttpCode {
  return (REDIRECT_HTTP_CODES as readonly unknown[]).includes(value);
}

export function isQueryParamsMode(value: unknown): value is QueryParamsMode {
  return (QUERY_PARAMS_MODES as readonly unknown[]).includes(value);
}

/**
 * Interprets a stored value: only `preserve` keeps the query. Records written
 * before validation existed store `''`, which has always meant drop.
 */
export function toQueryParamsMode(stored: unknown): QueryParamsMode {
  return stored === 'preserve' ? 'preserve' : 'drop';
}

/** An absolute URL with an allowed scheme; web URLs also need a host. */
export function isValidTargetUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (!(TARGET_URL_SCHEMES as readonly string[]).includes(url.protocol)) return false;
  return url.protocol === 'http:' || url.protocol === 'https:' ? url.hostname !== '' : url.pathname !== '';
}

export interface RedirectionFieldValues {
  readonly httpCode?: unknown;
  readonly queryParams?: unknown;
  /** Checked only when present; type-specific presence is validated separately. */
  readonly targetUrl?: string;
}

/** Messages for every invalid field among those present; empty when all are valid. */
export function redirectionFieldErrors(values: RedirectionFieldValues): string[] {
  const errors: string[] = [];
  if (values.httpCode !== undefined && !isRedirectHttpCode(values.httpCode)) {
    errors.push(`httpCode must be one of ${REDIRECT_HTTP_CODES.join(', ')}`);
  }
  if (values.queryParams !== undefined && !isQueryParamsMode(values.queryParams)) {
    errors.push(`queryParams must be one of ${QUERY_PARAMS_MODES.join(', ')}`);
  }
  if (values.targetUrl !== undefined && values.targetUrl.trim() !== '' && !isValidTargetUrl(values.targetUrl)) {
    errors.push(`targetUrl must be an absolute URL using ${TARGET_URL_SCHEMES.join(' ')}`);
  }
  return errors;
}
